import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Input,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Select,
  Checkbox,
  VStack,
  Text,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Flex,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomDatePicker from "./CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import ApiService from "../services/ApiService";
import { AuthContext } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faLocationDot,
  faHatCowboy,
  faBuilding,
  faUser,
  faUserTie,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

const defaultLivestock = {
  BeefCattle: 0,
  DairyCattle: 0,
  Buffalo: 0,
  Swine: 0,
  Chicken: 0,
  Duck: 0,
  Goat: 0,
  Sheep: 0,
  Quail: 0,
  Dog: 0,
  Cat: 0,
  Other: 0,
};

const EditFarmerProfile = () => {
  const navigate = useNavigate();
  const { profile, sessionId } = useContext(AuthContext) || {};
  const [formData, setFormData] = useState(null); // เริ่มต้นเป็น null เพื่อรอข้อมูล
  const [errors, setErrors] = useState({});
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [registrationType, setRegistrationType] = useState("individual"); // "individual" หรือ "corporate"
  const [contacts, setContacts] = useState([]);
  
  // ประเภทนิติบุคคล
  const corporateTypes = [
    "บริษัทจำกัด",
    "บริษัทมหาชนจำกัด",
    "ห้างหุ้นส่วนจำกัด",
    "ห้างหุ้นส่วนสามัญนิติบุคคล",
    "สหกรณ์",
    "วิสาหกิจชุมชน",
    "มูลนิธิ",
    "สมาคม",
    "อื่นๆ"
  ];

  // 1. ดึงข้อมูลผู้ใช้เมื่อคอมโพเนนต์โหลด
  useEffect(() => {
    if (profile?.FarmerID) {
      const fetchFarmerData = async () => {
        setIsLoading(true);
        try {
          const response = await ApiService({
            url: `/getFarmerProfileByID/${profile.FarmerID}`,
            method: "GET",
            sessionId: sessionId,
          });

          const data = response.data;
          // แปลงค่า BirthDate ที่ได้จาก API (String) ให้เป็น Date object
          // และตรวจสอบข้อมูล object ที่ซ้อนกันอยู่ให้มีค่าเริ่มต้นเสมอ
          setFormData({
            ...data,
            BirthDate: data.BirthDate || null,
            FarmAddress: data.FarmAddress || { AddressNo: "", AddressMoo: "", AddressRoad: "", AddressSubdistrict: "", AddressDistrict: "", AddressProvince: "", AddressZipcode: "" },
            Livestock: { ...defaultLivestock, ...(data.Livestock || {}) },
            CorporateType: data.CorporateType || "",
            CompanyBranch: data.CompanyBranch || "",
          });
          
          // ตั้งค่า registrationType จาก FarmerType
          setRegistrationType(data.FarmerType === "นิติบุคคล" ? "corporate" : "individual");
          
          // ตั้งค่า contacts สำหรับนิติบุคคล
          if (data.Contacts && data.Contacts.length > 0) {
            setContacts(data.Contacts);
          } else {
            setContacts([{ id: 1, ContactName: "", ContactPhone: "", ContactEmail: "", IsPrimary: true }]);
          }

        } catch (error) {
          console.error("Failed to fetch farmer data", error);
          alert("ไม่สามารถโหลดข้อมูลเกษตรกรได้");
        } finally {
          setIsLoading(false);
        }
      };
      fetchFarmerData();
    }
  }, [profile, sessionId]);

  // useEffect สำหรับจัดการการคัดลอกที่อยู่
  useEffect(() => {
    if (!formData) return; // ถ้ายังไม่มีข้อมูล ไม่ต้องทำอะไร

    if (useSameAddress) {
      setFormData((prev) => ({
        ...prev,
        FarmAddress: {
          AddressNo: prev.AddressNo,
          AddressMoo: prev.AddressMoo,
          AddressRoad: prev.AddressRoad,
          AddressSubdistrict: prev.AddressSubdistrict,
          AddressDistrict: prev.AddressDistrict,
          AddressProvince: prev.AddressProvince,
          AddressZipcode: prev.AddressZipcode,
        },
      }));
    }
  }, [useSameAddress, formData?.AddressNo]); // ตรวจสอบเมื่อ useSameAddress หรือที่อยู่หลักเปลี่ยน


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFarmAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      FarmAddress: { ...prev.FarmAddress, [name]: value },
    }));
  };

  const handleLivestockChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      Livestock: { ...prev.Livestock, [name]: value },
    }));
  };
  
  // Handler สำหรับเพิ่มผู้ติดต่อ
  const addContact = () => {
    const newId = contacts.length > 0 ? Math.max(...contacts.map(c => c.id || c.ContactID)) + 1 : 1;
    setContacts([...contacts, { 
      id: newId, 
      ContactName: "", 
      ContactPhone: "", 
      ContactEmail: "", 
      IsPrimary: false 
    }]);
  };
  
  // Handler สำหรับลบผู้ติดต่อ
  const removeContact = (id) => {
    if (contacts.length > 1) {
      const updatedContacts = contacts.filter(c => (c.id || c.ContactID) !== id);
      // ถ้าลบตัวที่เป็น primary ให้ตั้ง primary ใหม่
      if (!updatedContacts.some(c => c.IsPrimary) && updatedContacts.length > 0) {
        updatedContacts[0].IsPrimary = true;
      }
      setContacts(updatedContacts);
    }
  };
  
  // Handler สำหรับอัปเดตข้อมูลผู้ติดต่อ
  const handleContactChange = (id, field, value) => {
    setContacts(contacts.map(contact => 
      (contact.id || contact.ContactID) === id ? { ...contact, [field]: value } : contact
    ));
  };
  
  // Handler สำหรับตั้ง primary contact
  const setPrimaryContact = (id) => {
    setContacts(contacts.map(contact => ({
      ...contact,
      IsPrimary: (contact.id || contact.ContactID) === id
    })));
  };


  const isFormValid = () => {
    const baseValidation = 
      formData.FirstName?.trim() !== "" &&
      formData.IDCard?.trim() !== "" &&
      formData.Email?.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone?.trim() !== "" &&
      Object.keys(errors).length === 0;
    
    if (registrationType === "individual") {
      // บุคคลธรรมดา: ต้องมีนามสกุล
      return baseValidation && formData.LastName?.trim() !== "";
    } else {
      // นิติบุคคล: ต้องมีประเภทนิติบุคคล และต้องมีผู้ติดต่ออย่างน้อย 1 คน
      const hasValidContact = contacts.some(c => 
        c.ContactName?.trim() !== "" && 
        (c.ContactPhone?.trim() !== "" || c.ContactEmail?.trim() !== "")
      );
      return baseValidation && 
        formData.CorporateType?.trim() !== "" &&
        hasValidContact;
    }
  };

  // 2. ฟังก์ชันสำหรับบันทึกการแก้ไข
  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
      return;
    }

    // จัดการข้อมูลก่อนส่ง (ไม่ส่ง ProfileImage — แก้ไขรูปที่หน้า Profile แยกต่างหาก)
    const { ProfileImage, ...farmerFields } = formData;
    const submitData = {
        ...farmerFields,
        FarmerID: profile.FarmerID, 
        FarmerType: registrationType === "individual" ? "บุคคลธรรมดา" : "นิติบุคคล",
    };
    
    // เพิ่มข้อมูลผู้ติดต่อประสานงาน (สำหรับนิติบุคคล)
    if (registrationType === "corporate") {
      submitData.Contacts = contacts.filter(c => 
        c.ContactName?.trim() !== "" && 
        (c.ContactPhone?.trim() !== "" || c.ContactEmail?.trim() !== "")
      ).map(({ ContactID, ...rest }) => rest); // ลบ ContactID เก่าออก ใช้ id แทน
    }
    
    // ไม่ต้องแปลงวันที่อีก เพราะ CustomDatePicker จัดการให้แล้ว
    // หาก API ต้องการ Format เฉพาะ ให้แปลงที่นี่

    try {
      await ApiService({
        method: "POST", // หรือ 'PUT' ตามที่ API กำหนด
        url: "/updateFarmer",
        data: submitData,
        sessionId: sessionId,
      });
      alert("บันทึกการแก้ไขสำเร็จ!");
      navigate("/farmerprofile");
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + error.message);
    }
  };

  if (isLoading || !formData) {
    return (
      <Flex justify="center" align="center" height="80vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // --- START COPY FROM RegistrationFarmer ---
  const livestockTypes = [
    { key: "BeefCattle", label: "โคเนื้อ" }, { key: "DairyCattle", label: "โคนม" }, { key: "Buffalo", label: "กระบือ" },
    { key: "Swine", label: "สุกร" }, { key: "Chicken", label: "ไก่" }, { key: "Duck", label: "เป็ด" },
    { key: "Goat", label: "แพะ" }, { key: "Sheep", label: "แกะ" }, { key: "Quail", label: "นกกระทา" },
    { key: "Dog", label: "สุนัข" }, { key: "Cat", label: "แมว" }, { key: "Other", label: "สัตว์อื่นๆ" },
  ];

  const farmAddressFields = [
    { key: "AddressNo", label: "เลขที่" }, { key: "AddressMoo", label: "หมู่" }, { key: "AddressRoad", label: "ถนน" },
    { key: "AddressSubdistrict", label: "ตำบล" }, { key: "AddressDistrict", label: "อำเภอ" },
    { key: "AddressProvince", label: "จังหวัด" }, { key: "AddressZipcode", label: "รหัสไปรษณีย์" },
  ];
  // --- END COPY ---

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <StandardPageTitle title="แก้ไขข้อมูลเกษตรกร" />

      {/* Box 1: ข้อมูลทั่วไป */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลทั่วไป"
          bgColor="iTeal.600"
          icon={faAddressCard}
        />
        
        {/* แสดงรูปแบบการลงทะเบียน (ไม่สามารถแก้ไขได้) */}
        <Box p={5} mb={4}>
          <FormControl>
            <FormLabel fontSize="xl" color="gray" mb={3}>
              รูปแบบการลงทะเบียน
            </FormLabel>
            <HStack spacing={6}>
              <HStack spacing={2}>
                <FontAwesomeIcon icon={registrationType === "individual" ? faUser : faBuilding} />
                <Text fontSize="lg" fontWeight="bold">
                  {registrationType === "individual" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                </Text>
              </HStack>
            </HStack>
          </FormControl>
        </Box>
        
        <Divider mb={4} />
        
        {/* ฟอร์มสำหรับบุคคลธรรมดา */}
        {registrationType === "individual" && (
          <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6} p={5}>
            {["FirstName", "LastName", "IDCard", "Gender", "BirthDate"].map(
              (key) => (
                <GridItem colSpan={1} key={key}>
                  <FormControl 
                    isRequired={["FirstName", "LastName", "IDCard"].includes(key)} 
                    isInvalid={!!errors[key]}
                  >
                    <FormLabel fontSize="xl" color="gray">
                      {key === "FirstName"
                        ? "ชื่อ"
                        : key === "LastName"
                        ? "นามสกุล"
                        : key === "IDCard"
                        ? "เลขบัตรประชาชน"
                        : key === "Gender"
                        ? "เพศ"
                        : "วันเกิด (วัน/เดือน/ปี พ.ศ.)"}
                      {["FirstName", "LastName", "IDCard"].includes(key) && (
                        <Text as="span" color="red"> *</Text>
                      )}
                    </FormLabel>
                    {key === "Gender" ? (
                      <Select
                        name={key}
                        value={formData[key]}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      >
                        <option value="">-- เลือก --</option>
                        <option value="M">ชาย</option>
                        <option value="F">หญิง</option>
                      </Select>
                    ) : key === "BirthDate" ? (
                      <CustomDatePicker
                        name={key}
                        value={formData.BirthDate}
                        onChange={handleInputChange}
                        colorScheme="iTeal"
                      />
                    ) : (
                      <Input
                        name={key}
                        value={formData[key]}
                        onChange={handleInputChange}
                        isReadOnly={key === "IDCard"}
                        colorScheme="iTeal"
                      />
                    )}
                    <FormErrorMessage>{errors[key]}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              )
            )}
          </Grid>
        )}
        
        {/* ฟอร์มสำหรับนิติบุคคล */}
        {registrationType === "corporate" && (
          <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6} p={5}>
            {/* ประเภทนิติบุคคล */}
            <GridItem colSpan={1}>
              <FormControl isRequired isInvalid={!!errors.CorporateType}>
                <FormLabel fontSize="xl" color="gray">
                  ประเภทนิติบุคคล <Text as="span" color="red">*</Text>
                </FormLabel>
                <Select
                  name="CorporateType"
                  value={formData.CorporateType}
                  onChange={handleInputChange}
                  placeholder="-- เลือกประเภทนิติบุคคล --"
                  colorScheme="iTeal"
                >
                  {corporateTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.CorporateType}</FormErrorMessage>
              </FormControl>
            </GridItem>
            
            {/* ชื่อนิติบุคคล */}
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <FormControl isRequired isInvalid={!!errors.FirstName}>
                <FormLabel fontSize="xl" color="gray">
                  ชื่อนิติบุคคล <Text as="span" color="red">*</Text>
                </FormLabel>
                <Input
                  name="FirstName"
                  value={formData.FirstName}
                  onChange={handleInputChange}
                  placeholder="ใส่ชื่อเต็ม เช่น บริษัท ทดสอบ จำกัด"
                  colorScheme="iTeal"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  กรุณาใส่ชื่อเต็มของนิติบุคคล
                </Text>
                <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
              </FormControl>
            </GridItem>
            
            {/* สาขา */}
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  สาขา
                </FormLabel>
                <Input
                  name="CompanyBranch"
                  value={formData.CompanyBranch}
                  onChange={handleInputChange}
                  placeholder="สำนักงานใหญ่ / สาขาที่..."
                  colorScheme="iTeal"
                />
              </FormControl>
            </GridItem>
            
            {/* เลขทะเบียนนิติบุคคล */}
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <FormControl isRequired isInvalid={!!errors.IDCard}>
                <FormLabel fontSize="xl" color="gray">
                  เลขทะเบียนนิติบุคคล <Text as="span" color="red">*</Text>
                </FormLabel>
                <Input
                  name="IDCard"
                  value={formData.IDCard}
                  onChange={handleInputChange}
                  isReadOnly
                  colorScheme="iTeal"
                  bg="gray.100"
                />
                <FormErrorMessage>{errors.IDCard}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        )}
      </Box>
      
      {/* Box 1.5: ผู้ติดต่อประสานงาน (สำหรับนิติบุคคลเท่านั้น) */}
      {registrationType === "corporate" && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="ผู้ติดต่อประสานงาน"
            bgColor="iOrange.600"
            icon={faUserTie}
          />
          <VStack spacing={4} p={5} align="stretch">
            <Text color="gray.600" mb={2}>
              กรุณาระบุข้อมูลผู้ติดต่อประสานงาน (สามารถเพิ่มได้มากกว่า 1 คน)
            </Text>
            
            {contacts.map((contact, index) => (
              <Box 
                key={contact.id || contact.ContactID} 
                p={4} 
                borderWidth={1} 
                borderRadius="md" 
                borderColor={contact.IsPrimary ? "iOrange.300" : "gray.200"}
                bg={contact.IsPrimary ? "orange.50" : "white"}
              >
                <HStack justify="space-between" mb={3}>
                  <HStack>
                    <Badge colorScheme={contact.IsPrimary ? "orange" : "gray"}>
                      {contact.IsPrimary ? "ผู้ติดต่อหลัก" : `ผู้ติดต่อ ${index + 1}`}
                    </Badge>
                  </HStack>
                  <HStack spacing={2}>
                    {!contact.IsPrimary && (
                      <Button 
                        size="sm" 
                        colorScheme="orange" 
                        variant="outline"
                        onClick={() => setPrimaryContact(contact.id || contact.ContactID)}
                      >
                        ตั้งเป็นผู้ติดต่อหลัก
                      </Button>
                    )}
                    {contacts.length > 1 && (
                      <IconButton
                        aria-label="ลบผู้ติดต่อ"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeContact(contact.id || contact.ContactID)}
                      />
                    )}
                  </HStack>
                </HStack>
                
                <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={4}>
                  <GridItem>
                    <FormControl isRequired={index === 0}>
                      <FormLabel color="gray.600">
                        ชื่อ-นามสกุล {index === 0 && <Text as="span" color="red">*</Text>}
                      </FormLabel>
                      <Input
                        value={contact.ContactName}
                        onChange={(e) => handleContactChange(contact.id || contact.ContactID, 'ContactName', e.target.value)}
                        placeholder="ชื่อ-นามสกุล ผู้ติดต่อ"
                        colorScheme="iOrange"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel color="gray.600">เบอร์โทรศัพท์</FormLabel>
                      <Input
                        value={contact.ContactPhone}
                        onChange={(e) => handleContactChange(contact.id || contact.ContactID, 'ContactPhone', e.target.value)}
                        placeholder="0xx-xxx-xxxx"
                        colorScheme="iOrange"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel color="gray.600">อีเมล</FormLabel>
                      <Input
                        value={contact.ContactEmail}
                        onChange={(e) => handleContactChange(contact.id || contact.ContactID, 'ContactEmail', e.target.value)}
                        placeholder="email@example.com"
                        type="email"
                        colorScheme="iOrange"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            ))}
            
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              colorScheme="iOrange"
              variant="outline"
              onClick={addContact}
              alignSelf="flex-start"
            >
              เพิ่มผู้ติดต่อ
            </Button>
          </VStack>
        </Box>
      )}

      {/* Box 2: ข้อมูลการติดต่อ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader title="ข้อมูลการติดต่อ" bgColor="iBlue.600" icon={faLocationDot} />
        <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6} p={5}>
          {[ "AddressNo", "AddressMoo", "AddressRoad", "AddressSubdistrict", "AddressDistrict", "AddressProvince", "AddressZipcode", "Email", "Phone"].map((key) => (
            <GridItem colSpan={1} key={key}>
              <FormControl isRequired={["Email", "Phone"].includes(key)} isInvalid={!!errors[key]}>
                <FormLabel fontSize="xl" color="gray">
                  { key === "Email" ? "อีเมลล์" : key === "Phone" ? "เบอร์โทรศัพท์" : key === "AddressNo" ? "เลขที่" : key === "AddressMoo" ? "หมู่" : key === "AddressRoad" ? "ถนน" : key === "AddressSubdistrict" ? "ตำบล" : key === "AddressDistrict" ? "อำเภอ" : key === "AddressProvince" ? "จังหวัด" : key === "AddressZipcode" ? "รหัสไปรษณีย์" : "ที่อยู่"}
                  {["Email", "Phone"].includes(key) ? (<Text as="span" color="red"> *</Text>) : null}
                </FormLabel>
                <Input 
                  name={key} 
                  value={formData[key]} 
                  onChange={handleInputChange} 
                  isReadOnly={key === "Email"}
                  bg={key === "Email" ? "gray.100" : "white"}
                  colorScheme="iBlue"
                />
                {key === "Email" && (
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    อีเมลเป็น UserID ไม่สามารถแก้ไขได้
                  </Text>
                )}
                <FormErrorMessage>{errors[key]}</FormErrorMessage>
              </FormControl>
            </GridItem>
          ))}
        </Grid>
      </Box>
      
      {/* Box 3: ข้อมูลปศุสัตว์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader title="ข้อมูลปศุสัตว์" bgColor="iGreen.600" icon={faHatCowboy} />
          <VStack spacing={5} p={5} align="flex-start">
              <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6} w="100%">
                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                      <FormControl>
                      <FormLabel fontSize="xl" color="gray">ชื่อฟาร์ม / ชื่อบริษัท</FormLabel>
                      <Input 
                        name="FarmName" 
                        value={formData.FarmName} 
                        onChange={handleInputChange} 
                        colorScheme="iGreen"
                      />
                      </FormControl>
                  </GridItem>
              </Grid>
              <FormControl>
                  <FormLabel fontSize="xl" color="gray">สถานที่ตั้ง</FormLabel>
                  <Checkbox 
                    isChecked={useSameAddress} 
                    onChange={(e) => setUseSameAddress(e.target.checked)} 
                    colorScheme="iGreen"
                    size="lg"
                  >
                      ใช้ที่อยู่เดียวกับข้อมูลการติดต่อ
                  </Checkbox>
              </FormControl>
              <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6} w="100%">
              {farmAddressFields.map((field) => (
                  <GridItem colSpan={1} key={field.key}>
                      <FormControl>
                          <FormLabel color="gray.600">{field.label}</FormLabel>
                          <Input 
                            name={field.key} 
                            value={formData.FarmAddress[field.key]} 
                            onChange={handleFarmAddressChange} 
                            isDisabled={useSameAddress} 
                            colorScheme="iGreen"
                          />
                      </FormControl>
                  </GridItem>
              ))}
              </Grid>
              <FormControl>
              <FormLabel fontSize="xl" color="gray" mb={4}>จำนวนปศุสัตว์</FormLabel>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }} gap={5}>
                {livestockTypes.map((animal) => (
                  <GridItem key={animal.key}>
                    <FormControl>
                      <FormLabel color="gray.600">{animal.label}</FormLabel>
                      <NumberInput 
                        min={0} 
                        value={formData.Livestock[animal.key]} 
                        onChange={(valueString) => handleLivestockChange(animal.key, parseInt(valueString) || 0)}
                      >
                        <NumberInputField colorScheme="iGreen" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                ))}
              </Grid>
              </FormControl>
          </VStack>
      </Box>

      {/* ส่วนของปุ่ม */}
      <Box p={{ base: 2, md: 5 }} m={{ base: 2, md: 5 }}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justifyContent={{ md: "flex-end" }}
          alignItems={{ base: "stretch", md: "center" }}
          gap={4}
          w="100%"
        >
          <Button 
            colorScheme="gray" 
            variant="outline" 
            onClick={() => navigate("/farmerprofile")}
            w={{ base: "full", md: "auto" }}
          >
            ยกเลิก
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSubmit} 
            isDisabled={!isFormValid()}
            w={{ base: "full", md: "auto" }}
          >
            บันทึกการแก้ไข
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default EditFarmerProfile;